import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send, Anchor, Check, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BENEFITS = [
  "Kostenloses 30-minütiges Erstgespräch",
  "Konkrete erste Einschätzung deiner Kanäle",
  "Flexible Preise – keine starren Pauschalen",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Bitte bestätige die Datenschutzhinweise.");
      return;
    }
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, E-Mail und Nachricht sind Pflichtfelder.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        company: form.company || null,
        phone: form.phone || null,
      };
      const { data } = await axios.post(`${API}/contact`, payload);
      toast.success(data?.message || "Vielen Dank! Wir melden uns.");
      setForm({ name: "", company: "", email: "", phone: "", message: "", consent: false });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Etwas ist schiefgelaufen. Bitte versuche es erneut.";
      toast.error(typeof msg === "string" ? msg : "Etwas ist schiefgelaufen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="hh-section bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left column */}
        <div className="lg:col-span-5">
          <div className="hh-eyebrow mb-5">Kontakt</div>
          <h2 className="hh-headline text-[2.3rem] sm:text-[3rem] lg:text-[3.6rem] text-[#2E2E2E]">
            Zeit, <span className="hh-highlight">anzulegen.</span>
          </h2>
          <p className="font-body text-[#2E2E2E]/75 text-base sm:text-lg mt-6 max-w-md leading-relaxed">
            Erzähl uns kurz, worum es geht. Wir melden uns innerhalb von 24 Stunden mit
            einer ehrlichen Ersteinschätzung – kostenlos und unverbindlich.
          </p>

          <ul className="mt-8 space-y-3" data-testid="contact-benefits">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-3 font-body text-[#2E2E2E]">
                <span className="mt-1 w-5 h-5 rounded-full bg-[#FFF44F] flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-[#2E2E2E]" strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[#FFF44F] flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-[#2E2E2E]" />
              </div>
              <div>
                <div className="font-body text-xs text-[#2E2E2E]/60 uppercase tracking-[0.2em]">E-Mail</div>
                <a href="mailto:kontakt@hype-hafen.de" className="font-display text-[#2E2E2E] hover:text-[#2185D0] transition-colors">kontakt@hype-hafen.de</a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[#2E2E2E] flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-[#FFF44F]" />
              </div>
              <div>
                <div className="font-body text-xs text-[#2E2E2E]/60 uppercase tracking-[0.2em]">Standort</div>
                <span className="font-display text-[#2E2E2E]">Aus Hamburg für Hamburg</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Form card */}
        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="bg-[#2E2E2E] rounded-[28px] p-7 lg:p-10 text-white relative overflow-hidden"
          >
            <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-[#2185D0] opacity-20" aria-hidden />
            <div className="absolute -left-10 -bottom-10 w-44 h-44 rounded-full border-2 border-[#FFF44F]/20" aria-hidden />

            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#FFF44F] flex items-center justify-center">
                  <Anchor className="w-5 h-5 text-[#2E2E2E]" />
                </div>
                <div className="font-display text-xl">Kostenloses Erstgespräch</div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-xs text-white/60 mb-1.5">Name *</label>
                  <input
                    name="name"
                    data-testid="contact-name"
                    value={form.name}
                    onChange={handleChange}
                    className="hh-input"
                    placeholder="Max Mustermann"
                    required
                  />
                </div>
                <div>
                  <label className="block font-body text-xs text-white/60 mb-1.5">Firma</label>
                  <input
                    name="company"
                    data-testid="contact-company"
                    value={form.company}
                    onChange={handleChange}
                    className="hh-input"
                    placeholder="Musterfirma GmbH"
                  />
                </div>
                <div>
                  <label className="block font-body text-xs text-white/60 mb-1.5">E-Mail *</label>
                  <input
                    type="email"
                    name="email"
                    data-testid="contact-email"
                    value={form.email}
                    onChange={handleChange}
                    className="hh-input"
                    placeholder="max@firma.de"
                    required
                  />
                </div>
                <div>
                  <label className="block font-body text-xs text-white/60 mb-1.5">Telefon</label>
                  <input
                    name="phone"
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="hh-input"
                    placeholder="+49 ..."
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-body text-xs text-white/60 mb-1.5">Worum geht&apos;s? *</label>
                  <textarea
                    name="message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={handleChange}
                    className="hh-input"
                    placeholder="Beschreibe kurz dein Vorhaben, deine Ziele oder aktuelle Herausforderungen."
                    required
                  />
                </div>
              </div>

              <label className="mt-5 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  data-testid="contact-consent"
                  checked={form.consent}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 accent-[#FFF44F]"
                />
                <span className="font-body text-xs text-white/65 leading-relaxed">
                  Ich bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert werden.
                  Eine Weitergabe an Dritte erfolgt nicht.
                </span>
              </label>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="hh-btn-primary mt-7 w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Wird gesendet...</>
                ) : (
                  <>Anfrage senden <Send className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
